import React, { useRef, useEffect, useState, useCallback } from 'react';
import { COLORS } from '../constants';
import { useLives } from '../context/LivesContext';
import styles from './BreakoutGame.module.css';
import { useNavigate } from 'react-router-dom';

const GAME_WIDTH = 480;
const GAME_HEIGHT = 320;
const PADDLE_HEIGHT = 10;
const PADDLE_WIDTH = 75;
const BALL_RADIUS = 8;
const BRICK_ROW_COUNT = 5;
const BRICK_COLUMN_COUNT = 7;
const BRICK_WIDTH = 55;
const BRICK_HEIGHT = 20;
const BRICK_PADDING = 10;
const BRICK_OFFSET_TOP = 30;
const BRICK_OFFSET_LEFT = 30;

interface Brick {
  x: number;
  y: number;
  status: number;
}

type GameState = 'idle' | 'playing' | 'gameOver' | 'gameWon' | 'lifeLost';

const BreakoutGame: React.FC = () => {
  const { lives, addLives } = useLives();
  const canPlay = lives > 0;
  const [gameState, setGameState] = useState<GameState>('idle');
  const [rightPressed, setRightPressed] = useState(false);
  const [leftPressed, setLeftPressed] = useState(false);
  const [score, setScore] = useState(0);
  const [allowOneLastGame, setAllowOneLastGame] = useState(false);
  const [bricks, setBricks] = useState<Brick[][]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const paddleX = useRef((GAME_WIDTH - PADDLE_WIDTH) / 2);
  const ball = useRef({ x: GAME_WIDTH / 2, y: GAME_HEIGHT - 30, dx: 2, dy: -2 });
  const animationRef = useRef<number>();
  const [canvasDims, setCanvasDims] = useState({ width: GAME_WIDTH, height: GAME_HEIGHT });
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  // Responsive canvas size
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = Math.min(containerRef.current.offsetWidth, GAME_WIDTH);
        const height = (width / GAME_WIDTH) * GAME_HEIGHT;
        setCanvasDims({ width, height });
        setIsMobile(width < 400);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize bricks
  useEffect(() => {
    const newBricks: Brick[][] = [];
    for (let c = 0; c < BRICK_COLUMN_COUNT; c++) {
      newBricks[c] = [];
      for (let r = 0; r < BRICK_ROW_COUNT; r++) {
        newBricks[c][r] = { x: 0, y: 0, status: 1 };
      }
    }
    setBricks(newBricks);
  }, []);

  // Refactor draw to accept an optional overrideScore
  const draw = useCallback((overrideScore?: number) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvasDims.width, canvasDims.height);
    ctx.scale(canvasDims.width / GAME_WIDTH, canvasDims.height / GAME_HEIGHT);
    // Background
    ctx.fillStyle = COLORS.PURPLE || 'hsl(260,80%,74%)';
    ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    // Bricks
    for (let c = 0; c < BRICK_COLUMN_COUNT; c++) {
      for (let r = 0; r < BRICK_ROW_COUNT; r++) {
        if (bricks[c] && bricks[c][r] && bricks[c][r].status === 1) {
          const brickX = c * (BRICK_WIDTH + BRICK_PADDING) + BRICK_OFFSET_LEFT;
          const brickY = r * (BRICK_HEIGHT + BRICK_PADDING) + BRICK_OFFSET_TOP;
          bricks[c][r].x = brickX;
          bricks[c][r].y = brickY;
          ctx.beginPath();
          ctx.rect(brickX, brickY, BRICK_WIDTH, BRICK_HEIGHT);
          ctx.fillStyle = COLORS.WHITE;
          ctx.fill();
          ctx.closePath();
        }
      }
    }
    // Ball
    ctx.beginPath();
    ctx.arc(ball.current.x, ball.current.y, BALL_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = '#FFD700'; // Gold color for visibility
    ctx.fill();
    ctx.closePath();
    // Paddle
    ctx.beginPath();
    ctx.rect(paddleX.current, GAME_HEIGHT - (isMobile ? PADDLE_HEIGHT * 1.5 : PADDLE_HEIGHT), isMobile ? PADDLE_WIDTH * 1.3 : PADDLE_WIDTH, isMobile ? PADDLE_HEIGHT * 1.5 : PADDLE_HEIGHT);
    ctx.fillStyle = COLORS.WHITE;
    ctx.fill();
    ctx.closePath();
    // For score display:
    const displayScore = overrideScore !== undefined ? overrideScore : score;
    // Game over/win
    if (gameState === 'gameOver') {
      ctx.font = '32px monospace';
      ctx.fillStyle = COLORS.WHITE;
      ctx.fillText('Game Over', GAME_WIDTH / 2 - 90, GAME_HEIGHT / 2);
      ctx.font = '24px monospace';
      ctx.fillStyle = '#000000';
      const scoreText = 'Score: ' + displayScore;
      const scoreWidth = ctx.measureText(scoreText).width;
      ctx.fillText(scoreText, (GAME_WIDTH - scoreWidth) / 2, GAME_HEIGHT / 2 + 40);
      ctx.font = '20px monospace';
      ctx.fillStyle = COLORS.WHITE;
      ctx.fillText('Click or Space to Restart', GAME_WIDTH / 2 - 110, GAME_HEIGHT / 2 + 80);
    } else if (gameState === 'gameWon') {
      ctx.font = '32px monospace';
      ctx.fillStyle = COLORS.WHITE;
      ctx.fillText('You Win!', GAME_WIDTH / 2 - 70, GAME_HEIGHT / 2);
      ctx.font = '24px monospace';
      ctx.fillStyle = '#000000';
      const scoreText = 'Score: ' + displayScore;
      const scoreWidth = ctx.measureText(scoreText).width;
      ctx.fillText(scoreText, (GAME_WIDTH - scoreWidth) / 2, GAME_HEIGHT / 2 + 40);
      ctx.font = '20px monospace';
      ctx.fillStyle = COLORS.WHITE;
      ctx.fillText('Click or Space to Restart', GAME_WIDTH / 2 - 110, GAME_HEIGHT / 2 + 80);
    } else if (gameState === 'lifeLost') {
      ctx.font = '32px monospace';
      ctx.fillStyle = COLORS.WHITE;
      const scoreText = 'Score: ' + displayScore;
      const scoreWidth = ctx.measureText(scoreText).width;
      ctx.fillText(scoreText, (GAME_WIDTH - scoreWidth) / 2, GAME_HEIGHT / 2);
    } else if (gameState === 'idle' && (canPlay || allowOneLastGame)) {
      // Tap to Play text
      ctx.font = '28px monospace';
      ctx.fillStyle = '#000000';
      const tapText = '👆 Tap to Play!';
      const tapWidth = ctx.measureText(tapText).width;
      ctx.fillText(tapText, (GAME_WIDTH - tapWidth) / 2, GAME_HEIGHT / 2);
    }
  }, [canvasDims, bricks, ball, paddleX, isMobile, gameState, score, canPlay, allowOneLastGame]);

  // Collision detection
  const collisionDetection = () => {
    let bricksLeft = 0;
    for (let c = 0; c < BRICK_COLUMN_COUNT; c++) {
      for (let r = 0; r < BRICK_ROW_COUNT; r++) {
        const b = bricks[c] && bricks[c][r];
        if (b && b.status === 1) {
          bricksLeft++;
          if (
            ball.current.x > b.x &&
            ball.current.x < b.x + BRICK_WIDTH &&
            ball.current.y > b.y &&
            ball.current.y < b.y + BRICK_HEIGHT
          ) {
            ball.current.dy = -ball.current.dy;
            b.status = 0;
            setScore((s) => s + 1);
          }
        }
      }
    }
    if (bricksLeft === 0 && gameState === 'playing') {
      setGameState('gameWon');
      addLives(3); // Reward for winning
    }
  };

  // Main game loop
  const loop = () => {
    if (gameState !== 'playing') return;
    // Move ball
    ball.current.x += ball.current.dx;
    ball.current.y += ball.current.dy;
    // Wall collision
    if (ball.current.x + BALL_RADIUS > GAME_WIDTH || ball.current.x - BALL_RADIUS < 0) {
      ball.current.dx = -ball.current.dx;
    }
    if (ball.current.y - BALL_RADIUS < 0) {
      ball.current.dy = -ball.current.dy;
    } else if (ball.current.y + BALL_RADIUS > GAME_HEIGHT) {
      // Paddle collision
      const paddleW = isMobile ? PADDLE_WIDTH * 1.3 : PADDLE_WIDTH;
      if (
        ball.current.x > paddleX.current &&
        ball.current.x < paddleX.current + paddleW
      ) {
        ball.current.dy = -ball.current.dy;
      } else {
        // Ball missed: decrement a life and check for game over
        setGameState('lifeLost');
        setTimeout(() => {
          const newLives = lives - 1;
          addLives(-1);
          if (newLives > 0) {
            // Reset ball and paddle
            ball.current.x = GAME_WIDTH / 2;
            ball.current.y = GAME_HEIGHT - 30;
            ball.current.dx = 2;
            ball.current.dy = -2;
            paddleX.current = (GAME_WIDTH - PADDLE_WIDTH) / 2;
            setGameState('idle'); // Wait for user input to resume
          } else {
            setGameState('gameOver');
          }
        }, 1000);
        return;
      }
    }
    // Paddle movement
    if (rightPressed && paddleX.current < GAME_WIDTH - (isMobile ? PADDLE_WIDTH * 1.3 : PADDLE_WIDTH)) {
      paddleX.current += 7;
    } else if (leftPressed && paddleX.current > 0) {
      paddleX.current -= 7;
    }
    collisionDetection();
    draw();
    animationRef.current = requestAnimationFrame(loop);
  };

  // Start/restart game
  const startGame = () => {
    if (!canPlay && !allowOneLastGame) return;
    if (lives <= 0 && !allowOneLastGame) return; // Prevent starting if no lives
    setScore(0);
    setGameState('playing');
    // Reset bricks
    const newBricks: Brick[][] = [];
    for (let c = 0; c < BRICK_COLUMN_COUNT; c++) {
      newBricks[c] = [];
      for (let r = 0; r < BRICK_ROW_COUNT; r++) {
        newBricks[c][r] = { x: 0, y: 0, status: 1 };
      }
    }
    setBricks(newBricks);
    // Reset ball and paddle
    ball.current.x = GAME_WIDTH / 2;
    ball.current.y = GAME_HEIGHT - 30;
    ball.current.dx = 2;
    ball.current.dy = -2;
    paddleX.current = (GAME_WIDTH - PADDLE_WIDTH) / 2;
    if (lives === 1) {
      setAllowOneLastGame(true);
    }
  };

  // Start game loop when gameState changes to 'playing'
  useEffect(() => {
    if (gameState === 'playing') {
      loop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, isMobile, canvasDims]);

  // Show score and resume after life lost
  useEffect(() => {
    if (gameState === 'lifeLost') {
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx) draw(score);
    }
  }, [gameState, draw, score]);

  // Draw initial screen when idle
  useEffect(() => {
    if (gameState === 'idle') {
      draw();
    }
  }, [gameState, draw]);

  // Keyboard controls (update to block spacebar when out of lives)
  useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      if (!canPlay && !allowOneLastGame) return;
      if (lives <= 0 && !allowOneLastGame) return;
      if (e.key === 'Right' || e.key === 'ArrowRight') {
        setRightPressed(true);
      } else if (e.key === 'Left' || e.key === 'ArrowLeft') {
        setLeftPressed(true);
      } else if (e.key === ' ' && (gameState === 'gameOver' || gameState === 'gameWon')) {
        startGame();
      }
    };
    const keyUpHandler = (e: KeyboardEvent) => {
      if (e.key === 'Right' || e.key === 'ArrowRight') {
        setRightPressed(false);
      } else if (e.key === 'Left' || e.key === 'ArrowLeft') {
        setLeftPressed(false);
      }
    };
    window.addEventListener('keydown', keyDownHandler);
    window.addEventListener('keyup', keyUpHandler);
    return () => {
      window.removeEventListener('keydown', keyDownHandler);
      window.removeEventListener('keyup', keyUpHandler);
    };
    // eslint-disable-next-line
  }, [gameState, canPlay, allowOneLastGame, lives]);

  // Touch controls for mobile
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let lastTouchX: number | null = null;
    const handleTouchStart = (e: TouchEvent) => {
      if (gameState !== 'playing') return;
      if (e.touches.length === 1) {
        lastTouchX = e.touches[0].clientX;
      }
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (gameState !== 'playing') return;
      if (e.touches.length === 1 && lastTouchX !== null) {
        const rect = canvas.getBoundingClientRect();
        const touchX = e.touches[0].clientX - rect.left;
        // Move paddle center to touchX
        let newPaddleX = (touchX / rect.width) * GAME_WIDTH - (isMobile ? (PADDLE_WIDTH * 1.3) : PADDLE_WIDTH) / 2;
        newPaddleX = Math.max(0, Math.min(newPaddleX, GAME_WIDTH - (isMobile ? (PADDLE_WIDTH * 1.3) : PADDLE_WIDTH)));
        paddleX.current = newPaddleX;
      }
    };
    const handleTouchEnd = () => {
      lastTouchX = null;
    };
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [gameState, isMobile]);

  // Mouse controls
  useEffect(() => {
    const canvas = canvasRef.current;
    const mouseMoveHandler = (e: MouseEvent) => {
      if (gameState !== 'playing') return;
      const rect = canvas?.getBoundingClientRect();
      if (rect) {
        const relativeX = e.clientX - rect.left;
        let newPaddleX = (relativeX / rect.width) * GAME_WIDTH - (isMobile ? (PADDLE_WIDTH * 1.3) : PADDLE_WIDTH) / 2;
        newPaddleX = Math.max(0, Math.min(newPaddleX, GAME_WIDTH - (isMobile ? (PADDLE_WIDTH * 1.3) : PADDLE_WIDTH)));
        paddleX.current = newPaddleX;
      }
    };
    canvas?.addEventListener('mousemove', mouseMoveHandler);
    return () => {
      canvas?.removeEventListener('mousemove', mouseMoveHandler);
    };
  }, [gameState, isMobile]);

  // Prevent scrolling/zooming on touch
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const preventDefault = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();
    };
    canvas.addEventListener('touchmove', preventDefault, { passive: false });
    return () => {
      canvas.removeEventListener('touchmove', preventDefault);
    };
  }, []);

  // Handle clicks to start game
  const handleCanvasClick = () => {
    if ((!canPlay && !allowOneLastGame) || (lives <= 0 && !allowOneLastGame)) return;
    if ((gameState === 'gameOver' || gameState === 'gameWon') && (canPlay || allowOneLastGame)) {
      startGame();
    } else if ((gameState === 'idle' && (canPlay || allowOneLastGame))) {
      startGame();
    }
  };

  // Reset allowOneLastGame after last game is played and game is over
  useEffect(() => {
    if (gameState === 'gameOver' && allowOneLastGame) {
      setAllowOneLastGame(false);
    }
  }, [gameState, allowOneLastGame]);

  // No lives remaining screen
  if (!canPlay && !allowOneLastGame) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        background: '#181818', 
        borderRadius: 12, 
        boxShadow: '0 2px 12px #0004', 
        padding: isMobile ? 16 : 24, 
        maxWidth: isMobile ? '100%' : 440, 
        margin: '0 auto',
        width: isMobile ? 'calc(100vw - 32px)' : 'auto'
      }}>
        <h2 style={{ color: COLORS.WHITE, marginBottom: 12, fontSize: isMobile ? '1.5rem' : '2rem' }}>🎾 Breakout</h2>
        <div style={{ 
          background: '#333', 
          padding: '2rem', 
          borderRadius: 8, 
          textAlign: 'center',
          color: COLORS.WHITE,
          marginBottom: 16,
          fontSize: isMobile ? '0.9rem' : '1rem'
        }}>
          <p style={{ fontSize: isMobile ? '1rem' : '1.1rem', marginBottom: '1rem' }}>❌ No lives remaining!</p>
          <p style={{ fontSize: isMobile ? '0.8rem' : '0.9rem', color: '#aaa' }}>
            Complete quests to earn more lives and play again.
          </p>
        </div>
        <button
          onClick={() => navigate('/games')}
          style={{
            marginTop: 24,
            background: COLORS.PRIMARY,
            color: COLORS.WHITE,
            border: 'none',
            borderRadius: 8,
            padding: '0.8rem 2rem',
            fontSize: '1.1rem',
            fontWeight: 600,
            cursor: 'pointer',
            width: '100%',
            maxWidth: 300
          }}
        >
          Back to Games
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={styles.container}>
      <h2 className={styles.title}>🎾 Breakout</h2>
      <div className={styles.pill}>
        Cost: 1 <span role="img" aria-label="heart">❤️</span> | Lives remaining: {lives}
      </div>
      <div className={styles.instructions}>
        Press and drag the platform left or right to keep the ball from falling to the ground!<br />
        Break all the bricks to win!
      </div>
      <canvas
        ref={canvasRef}
        width={canvasDims.width}
        height={canvasDims.height}
        className={styles.canvas}
        style={{ cursor: (gameState === 'idle' || gameState === 'gameOver' || gameState === 'gameWon') ? 'pointer' : 'default' }}
        onClick={handleCanvasClick}
        tabIndex={0}
      />
      <button
        onClick={() => navigate('/games')}
        style={{
          marginTop: 24,
          background: 'hsl(260,80%,74%)',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          padding: '0.8rem 2rem',
          fontSize: '1.1rem',
          fontWeight: 600,
          cursor: 'pointer',
          width: '100%',
          maxWidth: 300
        }}
      >
        Back to Games
      </button>
    </div>
  );
};

export default BreakoutGame; 